import { ref, onMounted } from 'vue';
import logger from '../utils/logger';

const AD_BLOCK_DETECTED_KEY = 'adblock_detected';
const ANALYTICS_KEY = 'adblock_analytics_sent';

const ANALYTICS_ENDPOINT = import.meta.env.VITE_ANALYTICS_ENDPOINT || '';

/**
 * Send ad blocker analytics event
 */
async function trackAdBlockEvent(event, data = {}) {
    if (!ANALYTICS_ENDPOINT) return;

    try {
        await fetch(ANALYTICS_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                event: `adblock_${event}`,
                ...data,
                timestamp: new Date().toISOString(),
                url: window.location.href
            })
        });
    } catch (e) {
        logger.debug('Analytics tracking failed:', e);
    }
}

/**
 * Track ad blocker detection (only once per session)
 */
function trackAdBlockDetection() {
    if (localStorage.getItem(ANALYTICS_KEY)) return;
    
    localStorage.setItem(ANALYTICS_KEY, 'true');
    trackAdBlockEvent('detected', { source: 'useAdBlockDetector' });
    logger.info('Ad blocker detected, analytics event sent');
}

export function useAdBlockDetector() {
    const isAdBlockerDetected = ref(false);

    /**
     * Clear the stored ad block detection flag
     */
    const clearAdBlockDetection = () => {
        localStorage.removeItem(AD_BLOCK_DETECTED_KEY);
        isAdBlockerDetected.value = false;
        logger.info('Ad block detection cleared');
    };

    /**
     * Checks for ad blocker by creating a bait element
     */
    const detectAdBlocker = async () => {
        // Clear previous detection on each session to avoid false positives persisting forever
        localStorage.removeItem(AD_BLOCK_DETECTED_KEY);
        
        let detected = false;
        
        // Method 1: Bait Element (most reliable method)
        const bait = document.createElement('div');
        // Common ad classes that ad blockers target
        bait.setAttribute('class', 'pub_300x250 pub_300x250m pub_728x90 text-ad textAd text_ad text_ads text-ads text-ad-links ad-box ad-slot banner-ad');

        // Style to ensure it doesn't affect layout but is "visible" to checkers
        bait.style.setProperty('width', '1px', 'important');
        bait.style.setProperty('height', '1px', 'important');
        bait.style.setProperty('position', 'absolute', 'important');
        bait.style.setProperty('left', '-10000px', 'important');
        bait.style.setProperty('top', '-1000px', 'important');

        document.body.appendChild(bait);

        // Allow some time for the ad blocker to act
        await new Promise(resolve => setTimeout(resolve, 100));

        const baitStyles = window.getComputedStyle(bait);

        // If the blocker applies display: none, offsetParent is usually null
        // If the blocker applies visibility: hidden, we check that
        // If the blocker collapses the height, offsetHeight is 0
        if (
            bait.offsetParent === null ||
            bait.offsetHeight === 0 ||
            bait.offsetWidth === 0 ||
            baitStyles.getPropertyValue('display') === 'none' ||
            baitStyles.getPropertyValue('visibility') === 'hidden'
        ) {
            detected = true;
            logger.info('AdBlock detection: Bait element hidden');
        }

        document.body.removeChild(bait);

        // Note: Removed network-based detection as it causes false positives
        // due to connectivity issues, corporate firewalls, offline mode, etc.

        if (detected) {
            isAdBlockerDetected.value = true;
            localStorage.setItem(AD_BLOCK_DETECTED_KEY, 'true');
            trackAdBlockDetection();
            logger.info('Ad blocker detected');
        } else {
            logger.info('No ad blocker detected');
        }
    };

    onMounted(() => {
        // Delay check slightly to ensure page is loaded
        setTimeout(detectAdBlocker, 2000);
    });

    return {
        isAdBlockerDetected,
        clearAdBlockDetection
    };
}
